import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import AdminPageShell from '../AdminPageShell';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { fetchAdminApprenantActivityDetail } from '../../../services/academiaAdminService';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import { AcademicPanelCard } from './AcademicPanelCard';

type ActivityDetail = {
  id?: string;
  when?: string;
  categorie?: string;
  categorieLabel?: string;
  action?: string;
  apprenant?: {
    id?: string;
    label?: string;
    matricule?: string | null;
    telephone?: string | null;
    email?: string | null;
  };
  idInscription?: string | null;
  idReference?: string | null;
  detailsComplets?: string | null;
  detailsStructure?: unknown;
};

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR');
  } catch {
    return '—';
  }
}

function DetailGrid({ rows }: { rows: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {rows.map((row) => (
        <div key={row.label}>
          <dt className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{row.label}</dt>
          <dd className="mt-1 text-sm font-medium text-slate-900">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function renderStructuredDetails(structured: unknown) {
  if (!structured || typeof structured !== 'object') return null;
  const obj = structured as Record<string, unknown>;
  const contexte =
    obj.contexte && typeof obj.contexte === 'object' ? (obj.contexte as Record<string, unknown>) : null;
  const commentaire = typeof obj.commentaire === 'string' ? obj.commentaire : null;

  if (!contexte && !commentaire) return null;

  return (
    <div className="space-y-4">
      {commentaire ? (
        <p className="rounded-lg border border-outline-variant/20 bg-surface-container-low px-3 py-2 text-sm text-slate-800">
          {commentaire}
        </p>
      ) : null}
      {contexte ? (
        <DetailGrid
          rows={Object.entries(contexte).map(([key, value]) => ({
            label: key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()),
            value:
              value == null
                ? '—'
                : typeof value === 'object'
                  ? JSON.stringify(value)
                  : String(value),
          }))}
        />
      ) : null}
    </div>
  );
}

export default function ApprenantActivityDetailPanel() {
  const { activityId } = useParams<{ activityId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ActivityDetail | null>(null);

  const load = useCallback(async () => {
    if (!activityId) return;
    setLoading(true);
    setError(null);
    try {
      const row = await fetchAdminApprenantActivityDetail(activityId);
      setData(row);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Activité introuvable');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [activityId]);

  useEffect(() => {
    void load();
  }, [load]);

  useRegisterPortalRefresh(load);

  const title = data?.action ?? 'Détail de l\'activité';

  return (
    <AdminPageShell
      title={title}
      description="Journal des activités apprenants"
      sideCard={{
        label: 'Apprenant',
        value: data?.apprenant?.label ?? '—',
        helper: data?.categorieLabel ?? 'Détail',
        sideLinks: [{ label: 'Retour au journal', to: '/admin/activites-apprenants', icon: 'history' }],
      }}
      isLoading={loading && !data && !error}
      isError={Boolean(error)}
      errorMessage={error ?? undefined}
      onRetry={() => void load()}
      loadingMessage="Chargement…"
    >
      {error ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
      ) : null}

      {!error && data ? (
        <div className="space-y-4">
          <AcademicPanelCard title="Résumé" icon={<LucideSymbolIcon name="person" tone="primary" size={20} />}>
            <DetailGrid
              rows={[
                { label: 'Date', value: formatDate(data.when) },
                { label: 'Catégorie', value: data.categorieLabel ?? data.categorie ?? '—' },
                { label: 'Activité', value: data.action ?? '—' },
                {
                  label: 'Apprenant',
                  value: data.apprenant?.id ? (
                    <Link
                      to={`/admin/apprenants/${data.apprenant.id}`}
                      state={{ apprenantLabel: data.apprenant.label }}
                      className="text-primary hover:underline"
                    >
                      {data.apprenant.label}
                    </Link>
                  ) : (
                    '—'
                  ),
                },
                { label: 'Matricule', value: data.apprenant?.matricule ?? '—' },
                { label: 'Téléphone', value: data.apprenant?.telephone ?? '—' },
                { label: 'E-mail', value: data.apprenant?.email ?? '—' },
                { label: 'Inscription liée', value: data.idInscription ?? '—' },
                { label: 'Référence', value: data.idReference ?? '—' },
              ]}
            />
          </AcademicPanelCard>

          <AcademicPanelCard
            title="Détails enregistrés"
            icon={<LucideSymbolIcon name="description" tone="primary" size={20} />}
          >
            {renderStructuredDetails(data.detailsStructure) ?? (
              <pre className="max-h-96 overflow-auto rounded-lg bg-surface-container-high p-3 text-xs text-slate-800 whitespace-pre-wrap">
                {data.detailsComplets ?? 'Aucun détail supplémentaire.'}
              </pre>
            )}
          </AcademicPanelCard>
        </div>
      ) : null}
    </AdminPageShell>
  );
}
